import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, Check, Mail } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import ConfidentialityConfirm from "./ConfidentialityConfirm";
import DecodeText from "./DecodeText";
import Hud from "./HudOverlay";
import { waLink } from "../lib/whatsapp";
import WhatsAppIcon from "./WhatsAppIcon";
import { useBotGuard } from "../lib/botguard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "All-in-One Consultation — Full Launch",
  "Company Incorporation & Licensing",
  "AML / Compliance",
  "Trading Platform Providers",
  "Liquidity & Bridge Providers",
  "CRM Providers",
  "Payment Providers",
  "General Inquiry",
];

const Contact = () => {
  const bot = useBotGuard("inquiry");
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Invalid email";
    if (!form.service) e.service = "Select a service";
    if (!form.message.trim() || form.message.trim().length < 10)
      e.message = "Tell us a little more (min 10 chars)";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const attemptSubmit = (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setConfirmOpen(true);
  };

  const confirmSend = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, ...bot.fields() });
      bot.reset();
      setDone(true);
      setConfirmOpen(false);
      toast.success("Inquiry received. We'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", service: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative py-10 sm:py-14 lg:py-24 overflow-hidden"
      data-testid="contact-section"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#081024]/85 via-[#091228]/85 to-[#040816]/90" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(126,34,206,0.22) 0%, transparent 70%)" }} />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left intro */}
          <motion.div
            initial={{ opacity: 0, x: -70 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <div className="flex items-center gap-3 text-[11px] tracking-[0.5em] text-purple-400 uppercase">
              <span className="w-8 h-px bg-purple-500/60 neon-dash" />
              Contact
            </div>
            <h2 className="font-display mt-6 text-3xl sm:text-4xl lg:text-5xl text-zinc-100 leading-[1.05] tracking-tight">
              Let's design the
              <br />
              <em className="text-purple-400 not-italic text-glow"><DecodeText text="edge" /></em> together.
            </h2>
            <p className="mt-8 text-zinc-300 leading-relaxed max-w-md">
              Tell us what you&apos;re building or where it&apos;s stalled.
              Every inquiry is reviewed personally — every response is from a partner.
            </p>

            {/* Fee notice */}
            <div
              className="mt-6 flex items-start gap-3 rounded-xl border border-purple-500/25 bg-purple-600/[0.06] px-4 py-3.5 max-w-md"
              data-testid="contact-fee-notice"
            >
              <span className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full bg-purple-400 shrink-0" />
              <p className="text-xs text-zinc-200 leading-relaxed">
                <span className="text-purple-200 font-medium">Strategy Call — $250, 30 minutes.</span>
                {" "}Credited in full toward any engagement.
              </p>
            </div>

            <div className="mt-12 space-y-5">
              <div className="flex items-start gap-4 text-zinc-200">
                <div className="w-9 h-9 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-purple-300">
                  <WhatsAppIcon size={15} />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500">WhatsApp · Direct</div>
                  <a
                    href={waLink("Hi Ah-Jin Group — I'd like to book the $250 consultation (30 min strategy call).")}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp-link"
                    className="text-sm mt-1 block hover:text-purple-300 transition-colors"
                  >
                    +971 55 606 9046
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4 text-zinc-200">
                <div className="w-9 h-9 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-purple-300">
                  <Mail size={15} strokeWidth={1.4} />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500">CEO · Inquiries</div>
                  <a href="mailto:ceo@ah-jingroup.com" className="text-sm mt-1 block hover:text-purple-300 transition-colors">ceo@ah-jingroup.com</a>
                </div>
              </div>
              <div className="flex items-start gap-4 text-zinc-200">
                <div className="w-9 h-9 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-purple-300">
                  <Mail size={15} strokeWidth={1.4} />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500">Finance · Invoicing</div>
                  <a href="mailto:finance@ah-jingroup.com" className="text-sm mt-1 block hover:text-purple-300 transition-colors">finance@ah-jingroup.com</a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right form */}
          <div className="lg:col-span-7">
            <motion.form
              onSubmit={attemptSubmit}
              initial={{ opacity: 0, x: 70 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="relative bg-[#081428]/85 border border-white/10 rounded-3xl p-6 sm:p-8 lg:p-10 overflow-hidden"
              data-testid="contact-form"
            >
              <Hud />
              <div className="absolute -top-px left-10 right-10 h-px bg-gradient-to-r from-transparent via-purple-500/50 to-transparent" />

              {done ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center py-12"
                  data-testid="contact-success"
                >
                  <div className="w-14 h-14 mx-auto rounded-full border border-purple-500/40 bg-purple-600/10 flex items-center justify-center text-purple-300 mb-6 purple-glow">
                    <Check size={22} strokeWidth={1.6} />
                  </div>
                  <div className="font-display text-2xl text-zinc-100">Inquiry received</div>
                  <p className="text-zinc-300 mt-3 max-w-md mx-auto text-sm">
                    A partner will reach out within one business day. In the meantime,
                    feel free to send another note.
                  </p>
                  <button
                    type="button"
                    onClick={() => setDone(false)}
                    className="mt-8 text-xs uppercase tracking-[0.35em] text-purple-300 hover:text-purple-200"
                    data-testid="contact-send-another-btn"
                  >
                    Send another →
                  </button>
                </motion.div>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Name" error={errors.name}>
                      <input
                        data-testid="contact-name-input"
                        type="text"
                        value={form.name}
                        onChange={(e) => update("name", e.target.value)}
                        className="ahjin-input w-full px-4 py-3.5 rounded-lg outline-none text-sm"
                        placeholder="Your full name"
                      />
                    </Field>
                    <Field label="Email" error={errors.email}>
                      <input
                        data-testid="contact-email-input"
                        type="email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        className="ahjin-input w-full px-4 py-3.5 rounded-lg outline-none text-sm"
                        placeholder="you@firm.com"
                      />
                    </Field>
                    <Field label="Company" optional>
                      <input
                        data-testid="contact-company-input"
                        type="text"
                        value={form.company}
                        onChange={(e) => update("company", e.target.value)}
                        className="ahjin-input w-full px-4 py-3.5 rounded-lg outline-none text-sm"
                        placeholder="Optional"
                      />
                    </Field>
                    <Field label="Interested in" error={errors.service}>
                      <Select
                        value={form.service}
                        onValueChange={(v) => update("service", v)}
                      >
                        <SelectTrigger
                          className="ahjin-input w-full px-4 py-3.5 rounded-lg text-sm h-auto"
                          data-testid="contact-service-trigger"
                        >
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-950 border-white/10 text-zinc-100">
                          {SERVICE_OPTIONS.map((opt) => (
                            <SelectItem
                              key={opt}
                              value={opt}
                              className="focus:bg-purple-600/20 focus:text-white"
                              data-testid={`contact-service-option-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                            >
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>

                  <div className="mt-5">
                    <Field label="Tell us what you're working on" error={errors.message}>
                      <textarea
                        data-testid="contact-message-input"
                        value={form.message}
                        onChange={(e) => update("message", e.target.value)}
                        rows={6}
                        className="ahjin-input w-full px-4 py-3.5 rounded-lg outline-none text-sm resize-none"
                        placeholder="Stage, scope, timeline, anything that would help us prepare."
                      />
                    </Field>
                  </div>

                  <div className="mt-6 w-full">{bot.guard}</div>
                  <div className="mt-5 flex items-center justify-between gap-4 flex-wrap">
                    <p className="text-[11px] text-zinc-500 max-w-xs">
                      By submitting you agree to be contacted regarding your inquiry.
                      We do not share information.
                    </p>
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="contact-submit-btn"
                      className="foil-pill group inline-flex items-center gap-3 rounded-full px-8 py-4 text-xs uppercase tracking-[0.32em] text-zinc-50 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed overflow-hidden"
                    >
                      {loading ? (
                        <>
                          <Loader2 size={14} className="animate-spin" /> Sending
                        </>
                      ) : (
                        <>
                          Send Inquiry
                          <Send size={14} strokeWidth={1.6} className="group-hover:translate-x-0.5 transition-transform" />
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </motion.form>
          </div>
        </div>
      </div>
      <ConfidentialityConfirm
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={confirmSend}
        loading={loading}
      />
    </section>
  );
};

const Field = ({ label, optional, error, children }) => (
  <label className="block">
    <div className="flex items-center justify-between mb-2">
      <span className="text-[10px] uppercase tracking-[0.4em] text-zinc-500">
        {label}{optional && <span className="ml-2 text-zinc-600 normal-case">(optional)</span>}
      </span>
      {error && <span className="text-[10px] text-red-400">{error}</span>}
    </div>
    {children}
  </label>
);

export default Contact;
